/*
 * StoreNode — Per-variant gallery REPLACEMENT (Path A, universal own-gallery).
 *
 * Renders ONE per-variant gallery in the theme's gallery slot: a main image the
 * shopper can SWIPE / drag / arrow-key through, plus a thumbnail strip. To preserve the
 * theme's layout, we TAKE OVER the native gallery container(s): render our
 * gallery INSIDE one (inheriting its exact grid position + width) and hide the
 * containers' original children (inserting a sibling instead would add a stray
 * item to the theme's product grid and break the two-column layout). On variant
 * change we re-render with the selected variant's images (falling back to the
 * product's full media when a variant has no stored gallery). Theme-universal:
 * we render our own markup, so we don't depend on each theme's slider internals
 * — only on locating the native gallery container (prioritized selector list +
 * the per-store override the injector substitutes into the snippet).
 *
 * Reads per-variant image URLs from a <script type="application/json"> emitted
 * by storenode-variant-gallery.liquid. FAIL-SOFT: any failure leaves the native
 * gallery intact. Storefront JS cannot phone home (no external requests), so
 * mount state is exposed on <html> for a future probe: data-snvg="active" after
 * a successful takeover; data-snvg-miss="1" + data-snvg-selectors-tried when no
 * container matched; neither attribute when the widget never ran.
 */
(function () {
  'use strict';

  var DATA_ID = 'storenode-vpg-data';

  // Prioritized native-gallery container selectors, covering common OS 2.0 themes.
  var NATIVE_SELECTORS = [
    'media-gallery',                       // Dawn / Horizon custom element
    '.product-gallery',                    // Superstore + many paid themes
    '.product__media-wrapper',             // Dawn
    '.product__media-list',
    '.product-single__media-group',
    '.product-single__photos',
    '.product-media-gallery',
    '.product__photos',
    '.product-images',
    '[data-product-media-group]',
    '[data-product-single-media-group]'
  ];

  var VARIANT_EVENTS = [
    'variant:change', 'variantChange', 'on:variant:change',
    'product:variant-change', 'variant:update'
  ];

  // Product-form variant input, in preference order (first match in doc order).
  var INPUT_SELECTOR =
    'form[action*="/cart/add"] [name="id"], product-form [name="id"], [name="id"]';

  function parseData() {
    var el = document.getElementById(DATA_ID);
    if (!el) return null;
    var data;
    try { data = JSON.parse(el.textContent); } catch (e) { return null; }
    if (!data) return null;
    // The injector substitutes the per-store selector override into the snippet
    // at inject time. If the literal placeholder token ever leaks through
    // un-substituted, treat it as "no override" (auto-detect). Token assembled
    // in two parts so a find-and-replace over this file can never mangle the guard.
    if (data.selector === '__SNVG_' + 'SELECTOR__') data.selector = '';
    return data;
  }

  // display:none (including an ancestor's — e.g. a breakpoint-hidden
  // mobile/desktop twin) yields no boxes; a merely zero-height container still
  // yields one, so hidden-children containers stay eligible.
  function isVisible(el) {
    return !!(el && el.getClientRects && el.getClientRects().length);
  }

  // All native-gallery containers to take over. The per-store override
  // (data.selector, substituted by the injector) is tried FIRST, then the
  // built-in candidates; the first selector that matches anything wins. ALL of
  // that selector's distinct, non-nested matches are returned — themes with
  // separate mobile and desktop gallery DOM match twice, and hiding only one
  // would leave a stale native gallery on the other breakpoint. Conservative:
  // matches of lower-priority selectors are never mixed in.
  function findNativeGalleries(data) {
    var sels = data.selector ? [data.selector].concat(NATIVE_SELECTORS) : NATIVE_SELECTORS;
    for (var i = 0; i < sels.length; i++) {
      var nodes;
      try { nodes = document.querySelectorAll(sels[i]); } catch (e) { continue; }
      if (!nodes.length) continue;
      var out = [];
      for (var j = 0; j < nodes.length; j++) {
        var nested = false;
        for (var k = 0; k < out.length; k++) {
          if (out[k].contains(nodes[j])) { nested = true; break; }
        }
        if (!nested) out.push(nodes[j]);
      }
      return out;
    }
    return [];
  }

  // Swap the Shopify CDN width param so one emitted URL yields a responsive set
  // and a small thumbnail — avoids emitting multiple URLs per image in Liquid.
  function atWidth(src, w) {
    if (!src) return src;
    return /([?&]width=)\d+/.test(src)
      ? src.replace(/([?&]width=)\d+/, '$1' + w)
      : src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }
  function srcset(src) {
    return [320, 480, 640, 800, 1100, 1440, 1600]
      .map(function (w) { return atWidth(src, w) + ' ' + w + 'w'; }).join(', ');
  }

  var MAIN_SIZES = '(min-width: 750px) 60vw, 100vw';

  // Magnifier-plus glyph for the zoom affordance. Inline so the widget stays a
  // three-file drop with no image assets, and `currentColor` so it takes the
  // pill's text colour.
  var ZOOM_ICON =
    '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" focusable="false">' +
    '<circle cx="6" cy="6" r="4.6" stroke="currentColor" stroke-width="1.3"/>' +
    '<path d="M6 3.9v4.2M3.9 6h4.2M9.4 9.4 13 13" stroke="currentColor" stroke-width="1.3" ' +
    'stroke-linecap="round"/></svg>';

  // Warm the browser cache with an image at the SAME responsive size the visible
  // main will request (an off-DOM <img> with srcset+sizes selects by viewport+DPR,
  // because `sizes` vw units resolve against the viewport — so we cache exactly
  // what's about to be shown). Best-effort, no-op on a falsy src or one already
  // warmed. Since #608 the CURRENT variant's images are real <img>s in the track,
  // so they warm themselves; this is now what makes switching to ANOTHER variant
  // instant (_warmVariantLeads) — and it must keep using MAIN_SIZES/srcset, or it
  // would cache a URL the track never asks for.
  //
  // CRITICAL: an unreferenced `new Image()` can be GARBAGE-COLLECTED before its
  // fetch finishes, which ABORTS the preload — so the first real click still
  // cold-fetches (the "smooth only on the 2nd click, for some images" bug). We
  // therefore RETAIN each preloader in `_warming` until it loads/errors, and
  // dedupe so we never warm the same URL twice.
  var _warming = [];
  var _warmed = {};
  function warm(src) {
    if (!src) return;
    var url = atWidth(src, 1100);
    if (_warmed[url]) return;
    _warmed[url] = 1;
    var im = new Image();
    im.decoding = 'async';
    im.sizes = MAIN_SIZES;
    im.srcset = srcset(src);
    var done = function () {
      var k = _warming.indexOf(im);
      if (k !== -1) _warming.splice(k, 1);
    };
    im.onload = done;
    im.onerror = done;
    _warming.push(im);          // retain until loaded so GC can't abort the fetch
    im.src = url;
  }

  function StorenodeReplacementGallery(data, containers) {
    this.data = data;
    this.images = [];
    this.activeIndex = 0;
    this._zoom = { scale: 1, x: 0, y: 0 };   // expanded-view magnification (#610)
    this._lastSignal = null;    // null = landing (URL-first); 'url' | 'dom' after
    // This product's variant ids ("ids" blob = every variant; variants/selected
    // as a fallback for an older blob) — scopes the [name="id"] lookup so another
    // product's quick-shop form earlier in the DOM can't hijack the gallery.
    this._ids = {};
    var ids = (data.ids || []).concat(Object.keys(data.variants || {}));
    if (data.selected != null) ids.push(data.selected);
    for (var i = 0; i < ids.length; i++) this._ids[String(ids[i])] = 1;
    this._sync = this._sync.bind(this);
    this._onChange = this._onChange.bind(this);
    this._onUrlChange = this._signal.bind(this, 'url');
    this._onDomChange = this._signal.bind(this, 'dom');
    this._build();               // build our gallery element (once)
    this._takeOver(containers);  // mount inside a native container + hide their children
    this._patchHistory();
    this._bindSignals();
    this._sync();
    this._startPoll();
    this._warmVariantLeads();
  }

  // Warm the FIRST image of every variant (+ the default) once the browser is
  // idle, so SWITCHING to any variant shows its gallery instantly. Deferred so it
  // never competes with the initial render / LCP.
  StorenodeReplacementGallery.prototype._warmVariantLeads = function () {
    var self = this;
    var idle = window.requestIdleCallback || function (fn) { return setTimeout(fn, 200); };
    idle(function () {
      var vs = self.data.variants || {}, k;
      for (k in vs) { if (vs.hasOwnProperty(k) && vs[k] && vs[k][0]) warm(vs[k][0].src); }
      var d = self.data.default;
      if (d && d[0]) warm(d[0].src);
    });
  };

  StorenodeReplacementGallery.prototype._imagesFor = function (variantId) {
    var v = variantId && this.data.variants ? this.data.variants[variantId] : null;
    if (v && v.length) return v;
    return this.data.default || [];
  };

  StorenodeReplacementGallery.prototype._build = function () {
    var root = document.createElement('div');
    root.className = 'snvg';
    root.setAttribute('data-snvg', '');
    root.innerHTML =
      // The main image is a TRACK of every image in the current variant (#608),
      // filled by _render — so a swipe/drag carries the next image in with the
      // finger instead of sliding one lone <img> away and back again.
      '<div class="snvg__main"><button type="button" class="snvg__main-btn" data-snvg-main aria-label="Zoom">' +
      '<span class="snvg__track" data-snvg-track></span></button>' +
      // The affordance the native gallery had and we removed (#610): without it
      // nothing signals that the image opens.
      (this.data.zoom
        ? '<button type="button" class="snvg__expand" data-snvg-expand>' + ZOOM_ICON +
          '<span class="snvg__expand-click">Click to expand</span>' +
          '<span class="snvg__expand-tap">Tap to zoom</span></button>'
        : '') +
      '</div>' +
      '<div class="snvg__thumbs" data-snvg-thumbs role="list"></div>' +
      // The EXPANDED view is a gallery in its own right (#590): its own thumbnail
      // rail (vertical beside the image on wide screens, horizontal underneath on
      // narrow ones — CSS `order`, one DOM order) and its own gesture surface, so
      // every one of the four views can be navigated by both thumbnail and drag.
      (this.data.zoom
        ? '<dialog class="snvg__lightbox" data-snvg-lightbox>' +
          '<button type="button" class="snvg__close" data-snvg-close aria-label="Close">&times;</button>' +
          '<div class="snvg__lb-inner">' +
          '<div class="snvg__lb-stage" data-snvg-lb-stage>' +
          '<div class="snvg__lb-track" data-snvg-lb-track></div></div>' +
          '<div class="snvg__lb-thumbs" data-snvg-lb-thumbs role="list"></div>' +
          '</div></dialog>'
        : '');
    this.el = root;
    this.track = root.querySelector('[data-snvg-track]');
    this.thumbs = root.querySelector('[data-snvg-thumbs]');
    this.lightbox = root.querySelector('[data-snvg-lightbox]');
    this.lbTrack = root.querySelector('[data-snvg-lb-track]');
    this.lbStage = root.querySelector('[data-snvg-lb-stage]');
    this.lbThumbs = root.querySelector('[data-snvg-lb-thumbs]');
    this.lbInner = root.querySelector('.snvg__lb-inner');
    this._bindGalleryClicks();
    this._bindGestures();
  };

  // Take over EVERY matched container — hide each one's original children (a
  // theme with separate mobile/desktop gallery DOM would otherwise keep a stale
  // native gallery on the other breakpoint) — and mount our gallery inside the
  // first VISIBLE one, so we occupy the theme's exact gallery slot/width and
  // never disturb its product grid. Idempotent + safe to re-run after a theme
  // re-render (Section Rendering API) replaces a container's contents.
  StorenodeReplacementGallery.prototype._takeOver = function (containers) {
    this.natives = containers;
    var host = null;
    for (var i = 0; i < containers.length; i++) {
      var c = containers[i];
      c.setAttribute('data-snvg-host', '');
      this._hideNativeChildren(c);
      if (!host && isVisible(c)) host = c;
    }
    host = host || containers[0];
    if (!host.contains(this.el)) host.appendChild(this.el);
    this._rendered = false;   // (re)mounted → force the next _sync to render
  };

  // Hide + neutralize every direct child of a taken-over container that isn't
  // ours. Idempotent; also re-run from the MutationObserver so a deferred slider
  // APPENDING a node after mount can't resurrect a second (native) gallery.
  // Never touches anything outside a matched gallery container.
  StorenodeReplacementGallery.prototype._hideNativeChildren = function (container) {
    var kids = Array.prototype.slice.call(container.children);
    for (var i = 0; i < kids.length; i++) {
      var c = kids[i];
      if (c === this.el || c.hasAttribute('data-snvg-hidden')) continue;
      c.style.setProperty('display', 'none', 'important');
      c.setAttribute('data-snvg-hidden', '');
      this._neutralizeImages(c);
    }
  };

  // When several containers were taken over (mobile/desktop twins), keep our
  // gallery mounted in whichever one is visible at the CURRENT breakpoint —
  // otherwise a resize would show a container whose native children we hid and
  // whose slot we left empty.
  StorenodeReplacementGallery.prototype._relocate = function () {
    var cs = this.natives;
    if (!cs || cs.length < 2) return;
    var host = this.el.parentNode;
    if (host && document.contains(host) && isVisible(host)) return;
    for (var i = 0; i < cs.length; i++) {
      if (cs[i] !== host && document.contains(cs[i]) && isVisible(cs[i])) {
        cs[i].appendChild(this.el);
        return;
      }
    }
  };

  // Stop hidden native images from fetching (a display:none <img> can still load,
  // esp. the eager LCP image) — zero wasted bytes. Caches originals on data-attrs.
  StorenodeReplacementGallery.prototype._neutralizeImages = function (root) {
    if (!root || !root.querySelectorAll) return;
    var imgs = root.querySelectorAll('img');
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      if (img.hasAttribute('data-snvg-neutralized')) continue;
      if (img.getAttribute('src')) { img.setAttribute('data-snvg-src', img.getAttribute('src')); }
      if (img.getAttribute('srcset')) { img.setAttribute('data-snvg-srcset', img.getAttribute('srcset')); }
      img.setAttribute('data-snvg-neutralized', '');
      img.removeAttribute('srcset');
      img.removeAttribute('src');
      img.loading = 'lazy';
    }
    var sources = root.querySelectorAll('source');
    for (var s = 0; s < sources.length; s++) {
      var src = sources[s];
      if (src.hasAttribute('data-snvg-neutralized')) continue;
      if (src.getAttribute('srcset')) { src.setAttribute('data-snvg-srcset', src.getAttribute('srcset')); }
      src.setAttribute('data-snvg-neutralized', '');
      src.removeAttribute('srcset');
    }
  };

  StorenodeReplacementGallery.prototype._render = function (images) {
    // Drop any in-flight wrap (#621) FIRST: its reset timer would otherwise fire
    // over the new variant's track, and its loan bookkeeping lives on the track
    // element, which survives the innerHTML rebuild below.
    this._flushWrap();
    this._wrapping = 0;
    this.images = images || [];
    this.activeIndex = 0;
    if (!this.images.length) { this.el.hidden = true; return; }
    this.el.hidden = false;
    var self = this;

    // THE MAIN TRACK (#608) — every image of this variant, laid out side by side,
    // moved as one. Built FIRST so slide 0's fetch starts as early as possible
    // (it is the LCP image; the rest are marked low so they can't compete). These
    // are the same URLs `warm()` used to prefetch, so the track costs no extra
    // bytes — it just materialises what was already being fetched.
    this.track.innerHTML = '';
    this.images.forEach(function (img, i) {
      var im = document.createElement('img');
      im.className = 'snvg__main-img';
      im.decoding = 'async';
      im.alt = img.alt || '';
      im.sizes = MAIN_SIZES;
      im.srcset = srcset(img.src);
      im.src = atWidth(img.src, 1100);
      if (img.w && img.h) { im.width = img.w; im.height = img.h; }
      try { im.setAttribute('fetchpriority', i === 0 ? 'high' : 'low'); } catch (e) {}
      self.track.appendChild(im);
    });

    // The expanded view's track mirrors it, but its <img>s start EMPTY: those are
    // 1600px fetches, so nothing loads until the dialog is actually opened
    // (_hydrateLightbox fills a window around the active slide).
    if (this.lbTrack) {
      this.lbTrack.innerHTML = '';
      this.images.forEach(function (img) {
        var slide = document.createElement('div');
        slide.className = 'snvg__lb-slide';
        var im = document.createElement('img');
        im.className = 'snvg__lightbox-img';
        im.decoding = 'async';
        im.alt = img.alt || '';
        // Dimensions up front, even though the src arrives later: without them a
        // slide has NO layout until its 1600px file lands, so the expanded view
        // opened collapsed and snapped into place. The CSS keeps width/height
        // auto, so these only supply the aspect ratio to reserve space with.
        if (img.w && img.h) { im.width = img.w; im.height = img.h; }
        // Whether this image is worth magnifying can only be MEASURED once it
        // has loaded (natural vs displayed size, #618), and the cursor is the
        // shopper's only advance notice of it — so recompute on arrival rather
        // than leaving whatever the unmeasured guess produced.
        im.addEventListener('load', function () { self._applyZoom(); });
        slide.appendChild(im);
        self.lbTrack.appendChild(slide);
      });
    }

    // Both strips are built from the same images and drive the same activeIndex,
    // so the inline gallery and the expanded view never disagree (#590).
    var strips = [this.thumbs, this.lbThumbs];
    strips.forEach(function (strip) {
      if (!strip) return;
      strip.innerHTML = '';
      if (self.images.length < 2) { strip.hidden = true; return; }
      self.images.forEach(function (img, i) {
        var b = document.createElement('button');
        b.type = 'button';
        // Born ACTIVE at the current index (#607). _setMain marks the thumbs by
        // toggling the class on the EXISTING buttons, so it cannot mark buttons
        // this loop has not created yet — a strip rebuilt here has to carry the
        // state itself, or a freshly rendered gallery (landing, and every variant
        // change) shows NO active thumbnail until the shopper clicks something.
        b.className = 'snvg__thumb' + (i === self.activeIndex ? ' is-active' : '');
        b.setAttribute('role', 'listitem');
        b.setAttribute('aria-label', 'View image ' + (i + 1));
        b.innerHTML = '<img src="' + atWidth(img.src, 200) + '" alt="" loading="lazy" decoding="async">';
        b.addEventListener('click', function () { self._setMain(i); });
        strip.appendChild(b);
      });
      strip.hidden = false;
    });
    // Park both tracks on the first image. Never animated: a variant change
    // swaps the whole image set, so sliding between two unrelated galleries
    // would be nonsense.
    this._setMain(0, false);
  };

  // Show image `i` in BOTH views. `animate` defaults to "only when the move is to
  // a neighbour" — a one-step move slides (that is the whole point of the track),
  // while jumping from thumbnail 1 to thumbnail 7 cuts straight there rather than
  // blurring past five images. Pass it explicitly to force either behaviour.
  StorenodeReplacementGallery.prototype._setMain = function (i, animate) {
    var img = this.images[i];
    if (!img) return;
    var prev = this.activeIndex;
    this.activeIndex = i;
    if (animate === undefined) animate = Math.abs(i - prev) === 1;
    // INSTANT feedback: highlight the active thumb right away, before the (maybe
    // uncached) full-size image has loaded — so the click always feels responsive.
    // Both strips track the SAME index (#590), so navigating in either view leaves
    // the other on the image the shopper actually ended on.
    var strips = [this.thumbs, this.lbThumbs];
    for (var s = 0; s < strips.length; s++) {
      if (!strips[s]) continue;
      var ts = strips[s].querySelectorAll('.snvg__thumb');
      for (var t = 0; t < ts.length; t++) ts[t].classList.toggle('is-active', t === i);
      scrollIntoStrip(strips[s], ts[i]);
    }
    this._markVisible();
    this._slideTo(this.track, i, 0, animate);
    // The expanded view is kept in step even while CLOSED, so opening it never
    // starts on the wrong slide (hydration is what waits for `open`).
    this._setLightbox(i, animate);
  };

  // Tag the <img> each view is currently SHOWING. Storefront JS can't phone home,
  // so — like the `data-snvg` mount markers — the DOM is the only place this state
  // can be read from, by a probe or by the offline harness.
  //
  // It also carries the a11y state (#610): a track holds EVERY image of the
  // variant, so without this a screen reader would announce all of them as if they
  // were all on the page. Only the visible one is exposed.
  StorenodeReplacementGallery.prototype._markVisible = function () {
    var pairs = [[this.track, 'data-snvg-main-img'], [this.lbTrack, 'data-snvg-lightbox-img']];
    for (var p = 0; p < pairs.length; p++) {
      var container = pairs[p][0], attr = pairs[p][1];
      if (!container) continue;
      var imgs = container.querySelectorAll('img');
      for (var k = 0; k < imgs.length; k++) {
        var shown = k === this.activeIndex;
        if (shown) imgs[k].setAttribute(attr, '');
        else imgs[k].removeAttribute(attr);
        // Hide the off-screen slides from assistive tech, and from tab order.
        var slide = imgs[k].parentNode === container ? imgs[k] : imgs[k].parentNode;
        slide.setAttribute('aria-hidden', shown ? 'false' : 'true');
      }
    }
  };

  // Bring an element fully into its scrolling strip. The expanded view's rail
  // scrolls (horizontally when it sits under the image, vertically when beside
  // it), so navigating past the visible thumbnails would otherwise leave the
  // active one off screen — the reference theme scrolls it back deliberately.
  // Measured from rects rather than offsetLeft, which is relative to whichever
  // ancestor happens to be positioned at this breakpoint.
  function scrollIntoStrip(strip, el) {
    if (!strip || !el) return;
    try {
      var sr = strip.getBoundingClientRect(), er = el.getBoundingClientRect();
      if (!sr.width || !er.width) return;                 // no layout (offline harness)
      var pad = 8;
      if (strip.scrollWidth > strip.clientWidth + 1) {
        if (er.left < sr.left) strip.scrollLeft -= (sr.left - er.left) + pad;
        else if (er.right > sr.right) strip.scrollLeft += (er.right - sr.right) + pad;
      }
      if (strip.scrollHeight > strip.clientHeight + 1) {
        if (er.top < sr.top) strip.scrollTop -= (sr.top - er.top) + pad;
        else if (er.bottom > sr.bottom) strip.scrollTop += (er.bottom - sr.bottom) + pad;
      }
    } catch (e) {}
  }

  // Route every change signal through here with its SOURCE, so _sync can apply
  // last-signal-wins between the URL param and the product form's input.
  StorenodeReplacementGallery.prototype._signal = function (source) {
    this._lastSignal = source;
    this._onChange();
  };

  StorenodeReplacementGallery.prototype._variantIdFromUrl = function () {
    try {
      var v = new URLSearchParams(window.location.search).get('variant');
      if (v) return String(v);
    } catch (e) {}
    return null;
  };

  // Scoped read: only an input whose value-set intersects THIS product's variant
  // ids (see _findVariantInput). Null when no such input exists.
  StorenodeReplacementGallery.prototype._variantIdFromInput = function () {
    var input = this._findVariantInput(true);
    return input && input.value ? String(input.value) : null;
  };

  // Legacy unscoped read (pre-scoping behavior: first [name="id"] in doc order) —
  // used only when neither the URL nor a scoped input yields a variant id.
  StorenodeReplacementGallery.prototype._variantIdLoose = function () {
    var input = this._findVariantInput(false);
    return input && input.value ? String(input.value) : null;
  };

  // Find the product form's variant input, preferring one that provably belongs
  // to THIS product (its value-set intersects our variant ids — a SELECT's
  // options are its value-set; anything else is judged by its current value).
  // strict=true → null when nothing matches; strict=false → fall back to the
  // first candidate in doc order (the old behavior).
  StorenodeReplacementGallery.prototype._findVariantInput = function (strict) {
    var nodes;
    try { nodes = document.querySelectorAll(INPUT_SELECTOR); } catch (e) { return null; }
    for (var i = 0; i < nodes.length; i++) {
      if (this._inputMatchesProduct(nodes[i])) return nodes[i];
    }
    return strict ? null : (nodes.length ? nodes[0] : null);
  };

  StorenodeReplacementGallery.prototype._inputMatchesProduct = function (node) {
    if (node.tagName === 'SELECT' && node.options) {
      for (var i = 0; i < node.options.length; i++) {
        if (this._ids[String(node.options[i].value)]) return true;
      }
      return false;
    }
    return !!(node.value && this._ids[String(node.value)]);
  };

  // LAST-RESORT poll: some themes update variant state purely via JS property
  // writes (no URL change, no events, no childList mutation). Read the scoped
  // [name="id"] input at low frequency, only while the tab is visible. Skipped
  // entirely when the page has no input matching this product at mount.
  StorenodeReplacementGallery.prototype._startPoll = function () {
    if (!this._findVariantInput(true)) return;
    var self = this;
    this._pollTimer = setInterval(function () {
      if (document.visibilityState !== 'visible') return;
      var v = self._variantIdFromInput();
      if (v && v !== self._currentVariant) self._onDomChange();
    }, 800);
  };

  StorenodeReplacementGallery.prototype._sync = function () {
    this._relocate();
    // LAST-SIGNAL-WINS variant resolution. The URL param is authoritative only
    // at landing or when it was the most recent thing to change (history patch /
    // popstate); after any user-originated signal (change event, custom event,
    // observed re-render, poll) the product form's input wins — on themes that
    // never update the URL, a shopper landing with ?variant=X from a collection
    // link must not stay stuck on X's gallery forever.
    var fromUrl = this._variantIdFromUrl();
    var fromInput = this._variantIdFromInput();   // scoped to THIS product
    var id = this._lastSignal === 'dom' ? (fromInput || fromUrl) : (fromUrl || fromInput);
    if (!id) id = this._variantIdLoose();         // legacy unscoped fallback
    id = String(id || this.data.selected || '');
    // Only re-render when the VARIANT actually changes. Without this guard the
    // broad MutationObserver (and our own DOM writes during _render) retrigger
    // _render in a loop that constantly resets the gallery to the first image —
    // which made thumbnails look unclickable (every click was instantly undone).
    if (this._rendered && id === this._currentVariant) return;
    this._currentVariant = id;
    this._rendered = true;
    this._render(this._imagesFor(id || null));
  };

  StorenodeReplacementGallery.prototype._onChange = function () {
    var self = this;
    if (this._raf) cancelAnimationFrame(this._raf);
    this._raf = requestAnimationFrame(function () {
      self._raf = requestAnimationFrame(self._sync);
    });
  };

  StorenodeReplacementGallery.prototype._patchHistory = function () {
    ['pushState', 'replaceState'].forEach(function (type) {
      var orig = history[type];
      if (typeof orig !== 'function' || orig.__snvgPatched) return;
      var wrapped = function () {
        var r = orig.apply(this, arguments);
        window.dispatchEvent(new Event('snvg:locationchange'));
        return r;
      };
      wrapped.__snvgPatched = true;
      try { history[type] = wrapped; } catch (e) {}
    });
  };

  StorenodeReplacementGallery.prototype._bindSignals = function () {
    var self = this;
    window.addEventListener('snvg:locationchange', this._onUrlChange);
    window.addEventListener('popstate', this._onUrlChange);
    document.addEventListener('change', this._onDomChange);
    VARIANT_EVENTS.forEach(function (evt) { document.addEventListener(evt, self._onDomChange); });
    // A breakpoint change can swap WHICH taken-over container is visible
    // (mobile vs desktop gallery DOM) — _sync relocates us. Not a variant
    // signal, so the URL/input preference is left untouched.
    window.addEventListener('resize', this._onChange);
    // If the theme re-renders the product section (Section Rendering API), our
    // gallery gets detached → re-find the container(s) and take over again. A
    // deferred slider can also APPEND a node to a container we already took
    // over (double gallery) → re-hide unknown direct children. An observed
    // re-render is a DOM-side signal for last-signal-wins.
    try {
      this._mo = new MutationObserver(function () {
        if (!document.contains(self.el)) {
          var cs = findNativeGalleries(self.data);
          if (cs.length) self._takeOver(cs);
        } else if (self.natives) {
          for (var i = 0; i < self.natives.length; i++) {
            if (document.contains(self.natives[i])) self._hideNativeChildren(self.natives[i]);
          }
        }
        self._onDomChange();
      });
      this._mo.observe(document.body, { childList: true, subtree: true });
    } catch (e) {}
  };

  // Move the main image by `delta` positions, WRAPPING at both ends (#621).
  // It used to clamp — and that is what "the dragging suddenly stops working after
  // a few times" was: on a four-image gallery the fourth drag had nowhere to go,
  // so the track rubber-banded and sat there. The reference theme wraps in BOTH of
  // its views (`wrapAround: true` on the inline Flickity, `loop: true` on the
  // expanded PhotoSwipe), so a drag there never runs out of gallery.
  // Returns whether it moved (a single-image gallery still can't).
  StorenodeReplacementGallery.prototype._step = function (delta) {
    var n = this.images.length;
    if (n < 2) return false;
    var raw = this.activeIndex + delta;
    var i = ((raw % n) + n) % n;
    if (i === this.activeIndex) return false;
    // Remember that THIS move crosses the seam, so _slideTo animates onto the
    // lent slide instead of rewinding the track across every image between.
    this._wrapping = raw === i ? 0 : (raw > i ? 1 : -1);
    this._setMain(i, true);
    this._wrapping = 0;
    return true;
  };

  // ── CROSSING THE SEAM ──────────────────────────────────────────────────────
  // A wrap has to LOOK like an ordinary one-slide step: the first image entering
  // from the right when you drag past the last. But the track is a plain row of N
  // slides positioned by translateX(-100%*i), so moving from slide N-1 to slide 0
  // would animate the whole way back across the gallery.
  // The fix needs no clones: the target slide is temporarily LENT to the far side
  // with a transform of its own (slide 0 shifted N widths right sits immediately
  // after the last slide), the track animates one width onto that virtual
  // position, and then both are reset to the real ones with no transition — which
  // is invisible, because the two states paint identically.
  var SLIDE_MS = 280;

  StorenodeReplacementGallery.prototype._lend = function (track, dir) {
    var n = this.images.length;
    if (!track || !dir || n < 2) return;
    if (track._snvgLent === dir) return;          // already lent this way
    this._unlend(track);
    var j = dir > 0 ? 0 : n - 1;
    var slide = track.children[j];
    if (!slide) return;
    slide.style.transform = 'translateX(' + (dir > 0 ? n * 100 : -n * 100) + '%)';
    track._snvgLent = dir;
    // The lent slide is about to be dragged INTO view, and in the expanded view
    // it may have no source yet: the hydration window (#608) only covers the
    // current slide's neighbours, and the wrap target is at the opposite end of
    // the gallery. Without this the shopper drags in a blank frame that pops
    // once the step lands.
    if (track === this.lbTrack) this._hydrateOne(j);
  };

  StorenodeReplacementGallery.prototype._unlend = function (track) {
    if (!track || !track._snvgLent) return;
    var slide = track.children[track._snvgLent > 0 ? 0 : this.images.length - 1];
    if (slide) slide.style.transform = '';
    track._snvgLent = 0;
  };

  // Land a pending wrap NOW. Called before any new gesture and before a re-render:
  // the track sits at a virtual position until the reset timer fires, and anything
  // that repositions it in the meantime would jump.
  StorenodeReplacementGallery.prototype._flushWrap = function () {
    var tracks = [this.track, this.lbTrack];
    for (var k = 0; k < tracks.length; k++) {
      var t = tracks[k];
      if (!t || !t._snvgWrapT) continue;
      clearTimeout(t._snvgWrapT);
      t._snvgWrapT = 0;
      this._unlend(t);
      this._slideTo(t, this.activeIndex, 0, false);
    }
  };

  function prefersReducedMotion() {
    try {
      return !!(window.matchMedia &&
                window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    } catch (e) { return false; }
  }

  // Where a track sits to show slide `i`, plus the live drag offset. The track's
  // own width is ONE slide (its children overflow it), so a percentage resolves to
  // exactly one image — no layout read, and it stays correct through any resize.
  // The sign is written out rather than emitted as `+ -120px`, which not every
  // engine's calc() parser accepts.
  function trackTransform(i, dx) {
    var pct = -100 * i;
    var px = Math.round(dx || 0);
    if (!px) return pct ? 'translateX(' + pct + '%)' : '';
    return 'translateX(calc(' + pct + '% ' + (px < 0 ? '- ' + -px : '+ ' + px) + 'px))';
  }

  // Position a track. During a drag (`animate` false) it follows the finger with
  // no transition; on release it eases the REST OF THE WAY to wherever it landed —
  // continuing the shopper's movement rather than reversing it.
  StorenodeReplacementGallery.prototype._slideTo = function (track, i, dx, animate) {
    if (!track) return;
    var ease = animate && !prefersReducedMotion();
    var dir = this._wrapping || 0;
    // Crossing the seam (#621) — animate onto the LENT slide's virtual position,
    // one width away, then land on the real index once the movement is over.
    // With reduced motion there is no movement to shape, so it just cuts (and
    // the loan is dropped below, or the lent slide's real spot would be blank).
    if (dir && ease) {
      this._lend(track, dir);
      var self = this;
      track.style.transition = 'transform ' + (SLIDE_MS / 1000) + 's cubic-bezier(0.22, 0.61, 0.36, 1)';
      track.style.transform = trackTransform(dir > 0 ? this.images.length : -1, 0);
      clearTimeout(track._snvgWrapT);
      track._snvgWrapT = setTimeout(function () {
        track._snvgWrapT = 0;
        self._unlend(track);
        track.style.transition = 'none';
        track.style.transform = trackTransform(i, 0);
      }, SLIDE_MS);
      return;
    }
    // ANY settle or plain step drops a loan. `animate` is false only for the live
    // finger-follow — the one caller that must keep the lent slide in place — so
    // this is what stops a drag that lends and then does NOT wrap (too short, or
    // reversed mid-gesture) from leaving a slide parked at the far side, where
    // its real position would later show up blank.
    if (animate || dir) this._unlend(track);
    track.style.transition = ease
      ? 'transform ' + (SLIDE_MS / 1000) + 's cubic-bezier(0.22, 0.61, 0.36, 1)' : 'none';
    track.style.transform = trackTransform(i, dx);
  };

  // The per-surface finger-follow handed to _attachGesture: offset THIS view's
  // track from THIS view's current slide.
  StorenodeReplacementGallery.prototype._trackOffset = function (track) {
    var self = this;
    return function (px, settle) { self._slideTo(track, self.activeIndex, px, settle); };
  };

  // The expanded view fills the viewport like the Superstore theme's own zoom
  // (#591). Superstore paints a literal white there; we resolve the MERCHANT's
  // actual page background instead — on Superstore that IS white, so the result
  // is identical, but a dark storefront no longer gets a white slab. Walk up from
  // <body> to the first non-transparent colour; '' when nothing resolves, and the
  // CSS fallback (the old dark scrim) stands.
  var TRANSPARENT = /^(transparent|rgba\(\s*0\s*,\s*0\s*,\s*0\s*,\s*0\s*\))$/;
  function resolvedPageBackground() {
    try {
      var el = document.body;
      while (el) {
        var c = getComputedStyle(el).backgroundColor;
        if (c && !TRANSPARENT.test(c)) return c;
        el = el.parentElement;
      }
    } catch (e) {}
    return '';
  }

  StorenodeReplacementGallery.prototype._applyLightboxTheme = function () {
    var bg = resolvedPageBackground();
    if (!bg) return;
    try {
      // On the dialog for its own surface, and on <html> because ::backdrop does
      // not inherit from the dialog in every engine.
      this.lightbox.style.setProperty('--snvg-lb-bg', bg);
      document.documentElement.style.setProperty('--snvg-lb-bg', bg);
    } catch (e) {}
  };

  // Point the expanded view at image `i` — move its track, then fill in the
  // sources it now needs.
  StorenodeReplacementGallery.prototype._setLightbox = function (i, animate) {
    if (!this.lbTrack) return;
    // Changing slide always returns to 1x — carrying a zoom across images would
    // land the shopper on an arbitrary crop of the next one.
    this._resetZoom();
    this._slideTo(this.lbTrack, i, 0, animate);
    this._hydrateLightbox(i);
  };

  // The expanded view loads its own, larger (1600px) sources, so materialise only
  // what the shopper can see or reach with one gesture: the current slide and its
  // two neighbours. Nothing is fetched while the dialog is closed — otherwise
  // every product page would pull the full-size gallery on load.
  StorenodeReplacementGallery.prototype._hydrateLightbox = function (i) {
    if (!this.lbTrack) return;
    var last = this.lbTrack.children.length - 1;
    for (var k = Math.max(0, i - 1); k <= Math.min(last, i + 1); k++) this._hydrateOne(k);
  };

  StorenodeReplacementGallery.prototype._hydrateOne = function (k) {
    if (!this.lbTrack || !this.lightbox || !this.lightbox.open) return;
    var slide = this.lbTrack.children[k];
    var im = slide && slide.firstChild;
    if (im && !im.getAttribute('src') && this.images[k]) {
      im.src = atWidth(this.images[k].src, 1600);
    }
  };

  // ── CLICK-TO-ZOOM / PINCH in the expanded view (#610, reworked #618) ───────
  // The expanded view is where a shopper goes to look CLOSELY; the native gallery
  // we replaced magnified there, and ours only showed a bigger picture. Two
  // fingers scale, a tap toggles magnification about the tapped point, and while
  // zoomed a drag PANS instead of navigating.
  //
  // The interaction now mirrors the reference theme's expanded view, which is
  // PhotoSwipe v4 on its DEFAULTS (that theme passes no zoom options at all —
  // read from its own published sources). Three rules taken from it:
  //  1. A SINGLE click/tap on the image toggles zoom (`toggleDesktopZoom`, bound
  //     to a plain mouse tap on `.pswp__img`) — ours needed two, which nobody
  //     tries on a product photo.
  //  2. It zooms to the image's NATIVE pixel size, not to a fixed multiple:
  //     `getDoubleTapZoom` returns zoom level 1, i.e. 1:1. So it goes exactly as
  //     far as there is real detail to reveal and no further — "a little" on a
  //     desktop (our 1600px file shown ~900px wide → ~1.8x). Only when the image
  //     is ALREADY displayed near-native (fit ratio ≥ 0.7, so 1:1 would barely
  //     change anything) does it overshoot to 1.33x native, as PhotoSwipe does.
  //  3. The cursor is a state machine: zoom-in while a zoomable image sits at
  //     rest (`.pswp--zoom-allowed`), grab once zoomed (`.pswp--zoomed-in`),
  //     grabbing while actually dragging it (`.pswp--dragging`) — and nothing at
  //     all on an image with no detail to gain, which is also when the tap does
  //     nothing. A cursor must never promise magnification that isn't there.
  // ONE deliberate divergence (operator): PhotoSwipe reserves a single TOUCH tap
  // for toggling its auto-hiding chrome, so it needs a double tap on a phone.
  // Ours has no auto-hiding chrome, so a single tap zooms on touch as well.
  var ZOOM_MAX = 4;        // absolute ceiling, whatever the source's native size
  var ZOOM_UNMEASURED = 2; // fallback while the image has no layout / hasn't loaded
  var TAP_SLOP = 10;       // px of travel still counted as a tap, not a drag

  StorenodeReplacementGallery.prototype._zoomedImg = function () {
    return this.lbTrack ? this.lbTrack.querySelector('[data-snvg-lightbox-img]') : null;
  };

  // How far a tap should magnify THIS image — see rule 2 above. Returns 0 when
  // there is nothing to reveal (the image is already displayed at or past its
  // native size), which is what gates both the tap and the magnifier cursor.
  StorenodeReplacementGallery.prototype._zoomTarget = function () {
    var img = this._zoomedImg();
    if (!img) return 0;
    var shown = img.offsetWidth || 0;
    var natural = img.naturalWidth || 0;
    // Not measurable (mid-load, or an environment without layout). Assume the
    // image IS worth zooming: a first tap that silently did nothing would read
    // as a broken gallery, where an approximate magnification reads as fine.
    if (!shown || !natural) return ZOOM_UNMEASURED;
    var fit = shown / natural;              // PhotoSwipe's initialZoomLevel
    if (fit >= 1) return 0;                 // displayed at/over native: nothing to gain
    return Math.min(ZOOM_MAX, (fit < 0.7 ? 1 : 1.33) / fit);
  };

  StorenodeReplacementGallery.prototype._zoomable = function () {
    return this._zoomTarget() > 1.02;
  };

  StorenodeReplacementGallery.prototype._applyZoom = function () {
    var z = this._zoom || (this._zoom = { scale: 1, x: 0, y: 0 });
    // Write to EVERY slide, not just the active one: navigating away from a
    // zoomed image would otherwise leave its transform behind, so coming back to
    // it later would show it still magnified.
    var imgs = this.lbTrack ? this.lbTrack.querySelectorAll('img') : [];
    for (var k = 0; k < imgs.length; k++) {
      var active = imgs[k].hasAttribute('data-snvg-lightbox-img');
      imgs[k].style.transform = (active && z.scale !== 1)
        ? 'translate(' + Math.round(z.x) + 'px, ' + Math.round(z.y) + 'px) scale(' + z.scale + ')'
        : '';
      // The cursor state machine (rule 3 above). '' is deliberate for the
      // zoomable-at-rest case: clearing the inline style falls back to the
      // stylesheet's `cursor: zoom-in` on .snvg__lightbox-img (#617), so the
      // magnifier is stated once, in CSS. 'default' is the only case that has to
      // OVERRIDE that rule — an image with no detail to gain, whose tap is inert.
      imgs[k].style.cursor = !active ? ''
        : z.scale > 1 ? (this._panning ? 'grabbing' : 'grab')
        // Mid-pinch and momentarily back at 1x: leave the cursor as it is rather
        // than measuring the image, since _applyZoom runs on every pointermove
        // and reading offsetWidth right after writing a transform forces a style
        // flush. It settles the moment the gesture ends.
        : this._pinching ? imgs[k].style.cursor
        : this._zoomable() ? '' : 'default';
    }
    // Hand the browser back its vertical panning the moment we stop needing the
    // gesture, so a zoomed-out expanded view never blocks anything. '' restores
    // the stylesheet's `pan-y`.
    if (this.lbStage) this.lbStage.style.touchAction = z.scale > 1 ? 'none' : '';
  };

  StorenodeReplacementGallery.prototype._resetZoom = function () {
    this._zoom = { scale: 1, x: 0, y: 0 };
    this._applyZoom();
  };

  // Keep the pan inside the image: you can travel exactly as far as the scaled
  // image overhangs the stage, and no further.
  StorenodeReplacementGallery.prototype._clampZoom = function () {
    var z = this._zoom, img = this._zoomedImg();
    if (!img || !this.lbStage) return;
    try {
      var stage = this.lbStage.getBoundingClientRect();
      var w = img.offsetWidth, h = img.offsetHeight;
      var maxX = Math.max(0, (w * z.scale - stage.width) / 2);
      var maxY = Math.max(0, (h * z.scale - stage.height) / 2);
      z.x = Math.min(maxX, Math.max(-maxX, z.x));
      z.y = Math.min(maxY, Math.max(-maxY, z.y));
    } catch (e) {}
  };

  // Toggle magnification about a point, so a tap magnifies WHAT WAS TAPPED
  // rather than always the middle of the image.
  StorenodeReplacementGallery.prototype._toggleZoom = function (cx, cy) {
    var img = this._zoomedImg();
    if (!img) return;
    if (this._zoom && this._zoom.scale > 1) { this._resetZoom(); return; }
    var s = this._zoomTarget();
    // Nothing to reveal — do NOTHING, rather than blowing the image up past its
    // own resolution. The cursor has already said so (no magnifier).
    if (s <= 1.02) return;
    this._zoom = { scale: s, x: 0, y: 0 };
    try {
      var r = img.getBoundingClientRect();
      this._zoom.x = (r.left + r.width / 2 - cx) * (s - 1);
      this._zoom.y = (r.top + r.height / 2 - cy) * (s - 1);
    } catch (e) {}
    this._clampZoom();
    this._applyZoom();
  };

  StorenodeReplacementGallery.prototype._attachZoomPan = function (surface) {
    if (!surface || !window.PointerEvent) return;
    var self = this;
    var pts = {}, n = 0;
    var startDist = 0, startScale = 1, panFrom = null, downAt = null;

    function spread() {
      var a = [], k;
      for (k in pts) if (pts.hasOwnProperty(k)) a.push(pts[k]);
      if (a.length < 2) return 0;
      return Math.sqrt(Math.pow(a[0].x - a[1].x, 2) + Math.pow(a[0].y - a[1].y, 2));
    }

    surface.addEventListener('pointerdown', function (e) {
      if (!pts[e.pointerId]) n++;
      pts[e.pointerId] = { x: e.clientX, y: e.clientY };
      if (n === 1) {
        downAt = { x: e.clientX, y: e.clientY, t: e.timeStamp };
        panFrom = (self._zoom && self._zoom.scale > 1)
          ? { x: e.clientX, y: e.clientY, ox: self._zoom.x, oy: self._zoom.y } : null;
        // Pressing a zoomed image closes the hand — the reference theme's
        // `.pswp--dragging` cursor. It is set on the PRESS, not on the first
        // move, because that is when the shopper has taken hold of the image.
        if (panFrom) { self._panning = true; self._applyZoom(); }
      } else if (n === 2) {
        // A second finger means this is a pinch, never a swipe — _navBlocked()
        // makes the navigation layer stand down mid-gesture.
        self._pinching = true;
        startDist = spread();
        startScale = (self._zoom && self._zoom.scale) || 1;
        // …and never a TAP either (PhotoSwipe's `_isMultitouch` guard). Now that
        // ONE tap toggles zoom (#618), a pinch that ends with a finger releasing
        // near where it started would read as a tap and instantly undo the
        // magnification the shopper just pinched in. A clean pinch is already
        // safe — the first release clears the tap candidate on its way past —
        // but a CANCELLED finger (palm rejection mid-gesture) never goes through
        // that path, and the surviving finger's lift would fire the toggle.
        downAt = null;
      }
    });

    surface.addEventListener('pointermove', function (e) {
      if (!pts[e.pointerId]) return;
      pts[e.pointerId] = { x: e.clientX, y: e.clientY };
      if (n >= 2 && startDist > 0) {
        var d = spread();
        if (!d) return;
        self._zoom.scale = Math.min(ZOOM_MAX, Math.max(1, startScale * (d / startDist)));
        self._clampZoom();
        self._applyZoom();
        if (e.cancelable) e.preventDefault();
      } else if (panFrom && self._zoom.scale > 1) {
        self._zoom.x = panFrom.ox + (e.clientX - panFrom.x);
        self._zoom.y = panFrom.oy + (e.clientY - panFrom.y);
        self._clampZoom();
        self._applyZoom();
        if (e.cancelable) e.preventDefault();
      }
    });

    var release = function (e) {
      if (pts[e.pointerId]) { delete pts[e.pointerId]; n = Math.max(0, n - 1); }
      if (n < 2) startDist = 0;
      if (n === 0) {
        self._pinching = false;
        panFrom = null;
        // Let go → the hand opens again (grabbing → grab).
        if (self._panning) { self._panning = false; self._applyZoom(); }
        // Pinching down to 1x should land cleanly at centre rather than leaving a
        // sub-pixel offset that makes the image look nudged.
        if (self._zoom.scale <= 1.02 && self._zoom.scale !== 1) self._resetZoom();
      }
    };
    surface.addEventListener('pointerup', function (e) {
      var tap = downAt
        && Math.abs(e.clientX - downAt.x) < TAP_SLOP
        && Math.abs(e.clientY - downAt.y) < TAP_SLOP;
      release(e);
      if (!tap || n !== 0) { downAt = null; return; }
      downAt = null;
      // ON THE IMAGE ONLY. The stage spans the full width (#608), so the empty
      // space beside the image is click-outside-to-close — and now that ONE tap
      // toggles zoom, a tap anywhere on the stage would have eaten that. The
      // reference theme scopes it the same way (`.pswp__img` for zoom, the
      // surrounding surface for close). No click suppression is needed either:
      // the trailing click's target is the image, which the close handler
      // already ignores.
      var t = e.target;
      if (!t || !t.classList || !t.classList.contains('snvg__lightbox-img')) return;
      self._toggleZoom(e.clientX, e.clientY);
    });
    surface.addEventListener('pointercancel', release);
  };

  // True while the expanded view owns the gesture — zoomed in (a drag pans) or
  // mid-pinch. The navigation layer checks this and stands down.
  StorenodeReplacementGallery.prototype._navBlocked = function () {
    return !!(this._pinching || (this._zoom && this._zoom.scale > 1));
  };

  var SWIPE_SLOP = 8;      // px of travel before we commit to an axis
  var SWIPE_TRIGGER = 40;  // px of horizontal travel that counts as a swipe

  // SWIPE / DRAG navigation (#589). This gallery REPLACES the theme's native
  // (almost always swipeable) carousel, so without this a shopper's habitual
  // horizontal swipe on mobile did NOTHING — the only way to change image was
  // tapping a thumbnail, which is not what anyone tries first on a phone.
  // Reported live by a customer; reproduced with real touch input.
  //
  // Attached to BOTH surfaces, with one deliberate asymmetry (#590, operator
  // spec): the INLINE main image takes touch swipes but NOT mouse drags — on
  // desktop the page-level gallery is a click-thumbnails affair and a dragging
  // mouse should not change image — while inside the EXPANDED lightbox both
  // mouse drag and touch swipe navigate.
  //
  // What keeps this safe on a real storefront:
  //  * `touch-action: pan-y` (CSS) leaves VERTICAL panning to the browser, so the
  //    page scrolls normally — we never preventDefault a scroll.
  //  * The axis LOCKS on first significant travel: a mostly-vertical drag is
  //    abandoned outright and the page scrolls as usual.
  //  * A drag past the threshold SWALLOWS the click that follows it, so a swipe
  //    never also opens the zoom lightbox.
  //  * Single-image galleries bind nothing.
  //
  // `opts.allowMouse` — accept mouse/pen drags on this surface.
  // `opts.offset(px, settle)` — how this surface renders the live finger-follow.
  // `opts.track` — the track it moves, so a drag at an end can lend the wrap
  //   target to the far side before the finger gets there (#621).
  StorenodeReplacementGallery.prototype._attachGesture = function (surface, opts) {
    var self = this;
    if (!surface) return;
    opts = opts || {};
    var allowMouse = !!opts.allowMouse;
    var offset = opts.offset || function () {};
    var track = opts.track || null;
    var x0 = 0, y0 = 0, dx = 0, axis = '', active = false;

    // Clearing the swipe-suppression flag must happen on EVERY press, before any
    // gate — a press that this surface ignores (a mouse on the inline gallery,
    // where allowMouse is false) still ends the previous gesture, and if the flag
    // survived it would swallow that press's own click.
    var press = function () { self._suppressClick = false; };

    // A mouse drag on a surface that does not navigate (the inline gallery) is
    // still TRACKED — not to move anything, but so a deliberate drag doesn't end
    // in a click that pops the zoom open. Dragging there should do nothing at
    // all. A plain click, and the jitter of a click, still zoom normally.
    var navigate = true;

    var blocked = opts.blocked || function () { return false; };

    var start = function (x, y) {
      if (self.images.length < 2 || blocked()) return;
      // Land any wrap still mid-flight (#621) before this gesture repositions the
      // track — otherwise the finger-follow would start from a virtual position
      // and jump a whole slide width.
      self._flushWrap();
      active = true; axis = ''; dx = 0; x0 = x; y0 = y;
      if (navigate) offset(0, false);
    };

    // Returns true when we've taken ownership of the gesture (horizontal).
    var move = function (x, y) {
      if (!active) return false;
      // A gesture can BECOME the other view's business mid-flight — a second
      // finger lands and it is a pinch, not a swipe. Abandon cleanly rather than
      // fighting over the same pointers.
      if (blocked()) { active = false; offset(0, true); return false; }
      dx = x - x0;
      var dy = y - y0;
      if (!axis) {
        if (Math.abs(dx) < SWIPE_SLOP && Math.abs(dy) < SWIPE_SLOP) return false;
        axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        // Handed back to the page — put THIS surface's track back where it was
        // (the old code reset the inline main's, whichever surface was dragged).
        if (axis === 'y') { active = false; offset(0, false); return false; }
      }
      if (axis !== 'x') return false;
      if (navigate) {
        // At an end, LEND the wrap target to the far side before the track moves
        // (#621), so the finger drags the first image in from the right rather
        // than into blank space. This replaced the old dx/4 resistance, which was
        // the visible half of "dragging stops working": there was nothing beyond
        // the last image to drag to.
        var atEnd = (dx > 0 && self.activeIndex === 0) ||
                    (dx < 0 && self.activeIndex === self.images.length - 1);
        if (atEnd) self._lend(track, dx < 0 ? 1 : -1);
        offset(dx, false);
      }
      return navigate;
    };

    var end = function () {
      if (!active) { offset(0, false); return; }
      active = false;
      var swiped = (axis === 'x' && Math.abs(dx) > SWIPE_TRIGGER);
      var dir = dx < 0 ? 1 : -1;
      if (swiped) {
        // Suppress THIS gesture's trailing click so a swipe never also opens the
        // lightbox. The flag is normally cleared by the next `start()`, but that
        // only happens if the shopper's next press lands on this same surface —
        // so bound its lifetime too, and it can never eat an unrelated click.
        self._suppressClick = true;
        clearTimeout(self._suppressTimer);
        self._suppressTimer = setTimeout(function () { self._suppressClick = false; }, 400);
      }
      // CONTINUE the movement, never reverse it (#608). A committed swipe hands
      // off to _step, which animates the track the REST of the way to the next
      // slide from wherever the finger left it. Only a swipe that did not commit —
      // too short, or clamped at either end — eases back to the current slide.
      // Ordering matters: the old code settled to 0 FIRST and then swapped the
      // image, so the new one slid in from the side the shopper had dragged
      // towards, which is what made the transition read as backwards.
      if (navigate && !(swiped && self._step(dir))) offset(0, true);
      axis = ''; dx = 0;
    };

    if (window.PointerEvent) {
      var captured = false, pid = null;
      surface.addEventListener('pointerdown', function (e) {
        press();
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        navigate = allowMouse || e.pointerType !== 'mouse';
        pid = e.pointerId;
        captured = false;
        start(e.clientX, e.clientY);
      });
      surface.addEventListener('pointermove', function (e) {
        var owned = move(e.clientX, e.clientY);
        // Capture only ONCE THE GESTURE IS A CONFIRMED HORIZONTAL DRAG — never on
        // a plain press. Capturing on pointerdown retargets the subsequent CLICK
        // to the capturing element, so the zoom button never received it and a
        // simple click stopped opening the lightbox. By the time we capture, this
        // gesture is a swipe whose click we suppress anyway.
        if (owned && !captured && pid !== null) {
          captured = true;
          try { surface.setPointerCapture(pid); } catch (err) {}
        }
      });
      surface.addEventListener('pointerup', function () {
        if (captured && pid !== null) {
          try { surface.releasePointerCapture(pid); } catch (err) {}
        }
        captured = false; pid = null;
        end();
      });
      surface.addEventListener('pointercancel', function () {
        captured = false; pid = null;
        end();
      });
    } else {
      // Legacy fallback (pre-PointerEvent WebKit): touch-action may not be
      // honoured either, so an owned horizontal move preventDefaults explicitly.
      surface.addEventListener('touchstart', function (e) {
        press();
        navigate = true;               // touch always navigates
        var t = e.touches && e.touches[0];
        if (t) start(t.clientX, t.clientY);
      }, false);
      surface.addEventListener('touchmove', function (e) {
        var t = e.touches && e.touches[0];
        if (t && move(t.clientX, t.clientY) && e.cancelable) e.preventDefault();
      }, false);
      surface.addEventListener('touchend', end, false);
      surface.addEventListener('touchcancel', end, false);
    }

    // CAPTURE phase, so it runs before the main button's own lightbox handler.
    surface.addEventListener('click', function (e) {
      if (!self._suppressClick) return;
      self._suppressClick = false;
      e.stopPropagation();
      e.preventDefault();
    }, true);

  };

  // Attach the gesture layer to each surface, and the keyboard to the whole
  // widget. The asymmetry is deliberate (#590 — operator spec):
  //   INLINE main  → touch swipe only. On desktop the page-level gallery is
  //                  navigated by clicking thumbnails; a dragging mouse there
  //                  must NOT change image.
  //   EXPANDED view → mouse drag AND touch swipe both navigate.
  StorenodeReplacementGallery.prototype._bindGestures = function () {
    var self = this;
    this._attachGesture(this.el.querySelector('.snvg__main'), {
      allowMouse: false,
      offset: this._trackOffset(this.track),
      track: this.track,
    });
    this._attachGesture(this.lbStage, {
      allowMouse: true,
      offset: this._trackOffset(this.lbTrack),
      track: this.lbTrack,
      blocked: function () { return self._navBlocked(); },
    });
    this._attachZoomPan(this.lbStage);

    // Keyboard parity — the gallery is focusable (main button + thumbs), so
    // arrows must work there too. Without this the widget was unreachable by
    // keyboard beyond tabbing thumbnail to thumbnail. Works in BOTH views: the
    // <dialog> lives inside this.el, so its keydowns bubble here too.
    this.el.addEventListener('keydown', function (e) {
      var k = e.key;
      if (k !== 'ArrowRight' && k !== 'ArrowLeft') return;
      if (self.images.length < 2) return;
      e.preventDefault();
      self._step(k === 'ArrowRight' ? 1 : -1);
    });
  };

  StorenodeReplacementGallery.prototype._bindGalleryClicks = function () {
    var self = this;
    var mainBtn = this.el.querySelector('[data-snvg-main]');
    var expandBtn = this.el.querySelector('[data-snvg-expand]');
    if (this.data.zoom && this.lightbox && typeof this.lightbox.showModal === 'function') {
      var open = function () {
        if (!self.images[self.activeIndex]) return;
        self._applyLightboxTheme();
        // Open FIRST: the track is already parked on the current slide (_setMain
        // keeps it there while closed), and hydration is gated on `open`.
        try { self.lightbox.showModal(); } catch (e) {}
        self._setLightbox(self.activeIndex, false);
      };
      mainBtn.addEventListener('click', open);
      if (expandBtn) {
        expandBtn.addEventListener('click', function (e) {
          // The pill sits inside the gesture surface; without this its click also
          // reaches the surface and we'd open twice.
          e.stopPropagation();
          open();
        });
      }
      var close = function () { try { self.lightbox.close(); } catch (e) {} };
      var closeBtn = this.lightbox.querySelector('[data-snvg-close]');
      if (closeBtn) closeBtn.addEventListener('click', close);
      // Leaving a zoom behind would reopen mid-magnification on some other image.
      this.lightbox.addEventListener('close', function () { self._resetZoom(); });
      // Click-outside-the-image closes; a click on the image or the thumbnails is
      // navigation. The stage now spans the full width (#608), so the empty space
      // beside the image is a SLIDE rather than the inner wrapper it used to be —
      // both have to dismiss, or the click-away target would silently shrink to a
      // sliver. A drag's trailing click never reaches here (_attachGesture
      // swallows it in the capture phase).
      this.lightbox.addEventListener('click', function (e) {
        var t = e.target;
        if (t === self.lightbox || t === self.lbInner || t === self.lbStage ||
            (t.classList && t.classList.contains('snvg__lb-slide'))) close();
      });
    } else {
      mainBtn.style.cursor = 'default';
    }
  };

  function init() {
    if (document.querySelector('[data-snvg]')) return;   // already mounted (incl. the legacy app embed)
    var data = parseData();
    if (!data) return;
    var containers = findNativeGalleries(data);
    if (!containers.length) {
      // No matching gallery container. Leave a DOM marker a probe can read +
      // one console line, then stand down — the native gallery stays active.
      try {
        document.documentElement.dataset.snvgMiss = '1';
        document.documentElement.dataset.snvgSelectorsTried =
          String(NATIVE_SELECTORS.length + (data.selector ? 1 : 0));
        console.info('[storenode] gallery container not found — native gallery left active');
      } catch (e) {}
      return;
    }
    try {
      new StorenodeReplacementGallery(data, containers);
      document.documentElement.dataset.snvg = 'active';
    } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
